import { ChipMarker } from "@components/frame-editor/chip-markers";

/**
 * Configuration for chip overlay rendering
 */
interface ChipOverlayConfig {
  circleRadius?: number; // Radius of the number circle in pixels
  circleOffsetDistance?: number; // Distance from the point to the circle center
  arrowAngle?: number; // Arrow angle in degrees (45 = diagonal from top-left)
  backgroundColor?: string; // Semi-transparent background color
  borderColor?: string; // Border color for the circle
  numberColor?: string; // Color for the number text
  arrowColor?: string; // Color for the arrow line
  arrowHeadSize?: number; // Size of the arrow head
  lineWidth?: number; // Width of the arrow line
}

/**
Remove all the red and white arrows and circles.

At the red arrow number 1, using the reference number 1: Met le logo brodé sur une casquette sur la tête

At the red arrow number 2: Retire cet objet

At the red arrow number 3, using the reference number 3: Change la voiture

At the red arrow number 4: Change en t-shirt multi color
 */

const DEFAULT_CONFIG: Required<ChipOverlayConfig> = {
  circleRadius: 18,
  circleOffsetDistance: 100,
  arrowAngle: 225,
  backgroundColor: "rgba(255, 0, 0, 0.7)", // Transparent red
  borderColor: "rgba(255, 255, 255, 1)", // Solid white
  numberColor: "#ffffff", // White
  arrowColor: "rgba(255, 0, 0, 0.7)", // Red
  arrowHeadSize: 28,
  lineWidth: 2,
};

/**
 * Calculate the circle center position offset from the point
 */
const getCirclePosition = (
  pointX: number,
  pointY: number,
  canvasWidth: number,
  canvasHeight: number,
  offsetDistance: number,
  angleInDegrees: number
): { x: number; y: number } => {
  // Convert angle to radians (negative because canvas Y is inverted)
  // 45 degrees means going up and to the left from the point
  const angleRad = ((180 + angleInDegrees) * Math.PI) / 180;

  // Calculate offset position
  let circleX = pointX + Math.cos(angleRad) * offsetDistance;
  let circleY = pointY + Math.sin(angleRad) * offsetDistance;

  // Clamp to canvas bounds with some padding
  const padding = 30;
  circleX = Math.max(padding, Math.min(canvasWidth - padding, circleX));
  circleY = Math.max(padding, Math.min(canvasHeight - padding, circleY));

  return { x: circleX, y: circleY };
};

/**
 * Draw an arrow from start to end point
 */
const drawArrow = (
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  config: Required<ChipOverlayConfig>
) => {
  const { arrowColor, arrowHeadSize, lineWidth, borderColor } = config;

  // Calculate direction
  const dx = toX - fromX;
  const dy = toY - fromY;
  const angle = Math.atan2(dy, dx);

  // Move toX, toY to account for arrow head size
  const adjustedToX = toX - (arrowHeadSize - lineWidth * 2) * Math.cos(angle);
  const adjustedToY = toY - (arrowHeadSize - lineWidth * 2) * Math.sin(angle);

  // Draw the line
  ctx.fillStyle = arrowColor;
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.beginPath();
  const thikness = arrowHeadSize / 6;
  // Draw a rectangle as the arrow shaft
  ctx.moveTo(
    fromX - thikness * Math.sin(angle),
    fromY + thikness * Math.cos(angle)
  );
  ctx.lineTo(
    adjustedToX - thikness * Math.sin(angle),
    adjustedToY + thikness * Math.cos(angle)
  );
  ctx.lineTo(
    adjustedToX + thikness * Math.sin(angle),
    adjustedToY - thikness * Math.cos(angle)
  );
  ctx.lineTo(
    fromX + thikness * Math.sin(angle),
    fromY - thikness * Math.cos(angle)
  );
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Draw the arrow head
  ctx.fillStyle = arrowColor;
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = borderColor;
  ctx.beginPath();
  ctx.moveTo(toX, toY);
  ctx.lineTo(
    toX - arrowHeadSize * Math.cos(angle - Math.PI / 6),
    toY - arrowHeadSize * Math.sin(angle - Math.PI / 6)
  );
  ctx.lineTo(
    toX - arrowHeadSize * Math.cos(angle + Math.PI / 6),
    toY - arrowHeadSize * Math.sin(angle + Math.PI / 6)
  );
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
};

/**
 * Draw a circle with number
 */
const drawNumberCircle = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  number: number,
  config: Required<ChipOverlayConfig>
) => {
  const { circleRadius, backgroundColor, borderColor, numberColor, lineWidth } =
    config;

  // Draw filled circle with transparent background
  ctx.beginPath();
  ctx.arc(x, y, circleRadius, 0, 2 * Math.PI);
  ctx.fillStyle = backgroundColor;
  ctx.fill();

  // Draw circle border
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = lineWidth * 1.5;
  ctx.stroke();

  // Draw the number
  ctx.fillStyle = numberColor;
  ctx.font = `bold ${circleRadius}px Arial`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(number.toString(), x, y);
};

/**
 * Renders chip markers as an overlay on an image.
 * Each chip is represented as:
 * - A red circle with transparent red background containing the chip number in white
 * - A 45-degree arrow pointing from the circle to the actual position
 *
 * @param imageData - Base64 image data or image URL
 * @param chips - Array of chip markers with positions (x, y as fractions 0-1)
 * @param config - Optional configuration for styling
 * @returns Base64 encoded image with chip overlay
 */
export const renderChipOverlay = async (
  imageData: string,
  chips: ChipMarker[],
  config: ChipOverlayConfig = {}
): Promise<string> => {
  const mergedConfig: Required<ChipOverlayConfig> = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  // Load the original image
  const img = new Image();
  img.crossOrigin = "anonymous";

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = imageData;
  });

  // Create canvas with same dimensions as image
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  // Draw the original image
  ctx.drawImage(img, 0, 0);

  // Draw each chip marker
  for (const chip of chips) {
    // Convert fractional positions to pixel positions
    const pointX = chip.x * canvas.width;
    const pointY = chip.y * canvas.height;

    // Calculate circle position (offset from point at 45 degrees)
    const circlePos = getCirclePosition(
      pointX,
      pointY,
      canvas.width,
      canvas.height,
      mergedConfig.circleOffsetDistance,
      mergedConfig.arrowAngle
    );

    // Draw arrow from circle edge to point
    // Calculate where arrow starts (at circle edge, pointing toward target)
    const dx = pointX - circlePos.x;
    const dy = pointY - circlePos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const arrowStartX = circlePos.x + (dx / dist) * mergedConfig.circleRadius;
    const arrowStartY = circlePos.y + (dy / dist) * mergedConfig.circleRadius;

    drawArrow(ctx, arrowStartX, arrowStartY, pointX, pointY, mergedConfig);

    // Draw the number circle
    drawNumberCircle(ctx, circlePos.x, circlePos.y, chip.number, mergedConfig);
  }

  // Debug visualization
  const debug = document.location.search.indexOf("debug") !== -1;
  if (debug) {
    console.log("Chip overlay canvas debug", canvas);
    document.querySelectorAll("#debug-chip-overlay").forEach((a) => a.remove());
    const debugCanvas = document.createElement("canvas");
    debugCanvas.id = "debug-chip-overlay";
    debugCanvas.width = canvas.width;
    debugCanvas.height = canvas.height;
    debugCanvas.getContext("2d")?.drawImage(canvas, 0, 0);
    debugCanvas.style.position = "fixed";
    debugCanvas.style.top = "10px";
    debugCanvas.style.left = "220px";
    debugCanvas.style.width = "200px";
    debugCanvas.style.height = "auto";
    debugCanvas.style.border = "2px solid red";
    debugCanvas.style.zIndex = "9999";
    debugCanvas.style.pointerEvents = "none";
    debugCanvas.style.opacity = "0.9";
    debugCanvas.style.borderRadius = "8px";
    document.body.appendChild(debugCanvas);
  }

  // Convert to base64
  const resultData = canvas.toDataURL("image/png");

  // Clean up
  if (!debug) {
    canvas.remove();
  }

  return resultData;
};

/**
 * Creates chip overlay from an image file/URL and chips array
 * Returns only the overlay without the original image (transparent background)
 */
export const renderChipOverlayOnly = async (
  width: number,
  height: number,
  chips: ChipMarker[],
  config: ChipOverlayConfig = {}
): Promise<string> => {
  const mergedConfig: Required<ChipOverlayConfig> = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  // Create canvas with specified dimensions
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  // Transparent background
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw each chip marker
  for (const chip of chips) {
    // Convert fractional positions to pixel positions
    const pointX = chip.x * canvas.width;
    const pointY = chip.y * canvas.height;

    // Calculate circle position (offset from point at 45 degrees)
    const circlePos = getCirclePosition(
      pointX,
      pointY,
      canvas.width,
      canvas.height,
      mergedConfig.circleOffsetDistance,
      mergedConfig.arrowAngle
    );

    // Draw arrow from circle edge to point
    const dx = pointX - circlePos.x;
    const dy = pointY - circlePos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const arrowStartX = circlePos.x + (dx / dist) * mergedConfig.circleRadius;
    const arrowStartY = circlePos.y + (dy / dist) * mergedConfig.circleRadius;

    drawArrow(ctx, arrowStartX, arrowStartY, pointX, pointY, mergedConfig);

    // Draw the number circle
    drawNumberCircle(ctx, circlePos.x, circlePos.y, chip.number, mergedConfig);
  }

  // Convert to base64
  const resultData = canvas.toDataURL("image/png");

  // Clean up
  canvas.remove();

  return resultData;
};

/**
 * Applies chip overlay directly to an existing canvas context.
 * Used by renderImage to add chip markers to the rendered image.
 *
 * @param ctx - Canvas 2D rendering context to draw on
 * @param chips - Array of chip markers with positions (x, y as fractions 0-1)
 * @param config - Optional configuration for styling
 */
export const applyChipOverlayToCanvas = (
  ctx: CanvasRenderingContext2D,
  chips: ChipMarker[],
  config: ChipOverlayConfig = {}
): void => {
  if (!chips || chips.length === 0) return;

  const mergedConfig: Required<ChipOverlayConfig> = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  const canvasWidth = ctx.canvas.width;
  const canvasHeight = ctx.canvas.height;

  // Draw each chip marker
  for (const chip of chips) {
    // Convert fractional positions to pixel positions
    const pointX = chip.x * canvasWidth;
    const pointY = chip.y * canvasHeight;

    // Calculate circle position (offset from point at 45 degrees)
    const circlePos = getCirclePosition(
      pointX,
      pointY,
      canvasWidth,
      canvasHeight,
      mergedConfig.circleOffsetDistance,
      mergedConfig.arrowAngle
    );

    // Draw arrow from circle edge to point
    const dx = pointX - circlePos.x;
    const dy = pointY - circlePos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const arrowStartX = circlePos.x + (dx / dist) * mergedConfig.circleRadius;
    const arrowStartY = circlePos.y + (dy / dist) * mergedConfig.circleRadius;

    drawArrow(ctx, arrowStartX, arrowStartY, pointX, pointY, mergedConfig);

    // Draw the number circle
    drawNumberCircle(ctx, circlePos.x, circlePos.y, chip.number, mergedConfig);
  }
};
